import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { LuPencil, LuKeyRound, LuUserX, LuUserCheck } from "react-icons/lu";
import { API, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle, fieldStyle } from "../shared/styles";
import { SimpleModal } from "../shared/SimpleModal";

// Staff screen for client users — the people outside the business who get a
// login to their own building and nothing else.
//
// Shaped after settings/UsersSettings.js rather than inventing a second idiom
// for managing accounts: same table, same modals, same row actions. The one
// thing that screen has no equivalent for is the site list, which is the whole
// point of this one — a client user sees exactly the sites ticked here.
//
// Management/admin only; the backend enforces that too.
const COLS = "1.2fr 1fr 1.4fr 1.6fr 90px 110px";

export function ClientUsersPage() {
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [pwClient, setPwClient] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState("");

  const load = useCallback(() => {
    Promise.all([
      axios.get(`${API}/building-managers/`),
      axios.get(`${API}/sites/`),
    ])
      .then(([c, s]) => {
        setClients(c.data || []);
        setSites(s.data || []);
      })
      .catch(() => setError("Could not load client users."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleSite = (id) =>
    setForm((f) => ({
      ...f,
      siteIds: f.siteIds.includes(id)
        ? f.siteIds.filter((x) => x !== id)
        : [...f.siteIds, id],
    }));

  const openAdd = () => {
    setEditing(null);
    setForm(blankForm());
    setFormError("");
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      username: c.username,
      firstName: c.first_name || "",
      lastName: c.last_name || "",
      email: c.email || "",
      password: "",
      siteIds: c.sites || [],
    });
    setFormError("");
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!editing && !form.username.trim())
      return setFormError("Username is required.");
    if (!form.email.trim()) return setFormError("Email is required.");
    if (!editing && form.password.length < 8)
      return setFormError("Password must be at least 8 characters.");
    if (form.siteIds.length === 0)
      return setFormError("Pick at least one site.");

    setSaving(true);
    setFormError("");
    try {
      if (editing) {
        // Username is immutable and the password has its own dialog, the same
        // split the staff Users screen uses.
        await axios.patch(`${API}/building-managers/${editing.id}/`, {
          first_name: form.firstName,
          last_name: form.lastName,
          email: form.email,
          sites: form.siteIds,
        });
      } else {
        await axios.post(`${API}/building-managers/`, {
          username: form.username.trim(),
          first_name: form.firstName,
          last_name: form.lastName,
          email: form.email,
          password: form.password,
          sites: form.siteIds,
        });
      }
      setShowModal(false);
      load();
    } catch (err) {
      setFormError(firstMessage(err, "Could not save that client user."));
      setSaving(false);
      return;
    }
    setSaving(false);
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 8)
      return setPwError("Password must be at least 8 characters.");
    setPwSaving(true);
    setPwError("");
    try {
      await axios.post(
        `${API}/building-managers/${pwClient.id}/reset-password/`,
        { new_password: newPassword }
      );
      setPwClient(null);
      setNewPassword("");
    } catch (err) {
      setPwError(firstMessage(err, "Could not reset that password."));
    }
    setPwSaving(false);
  };

  const toggleAccess = async (c) => {
    const action = c.is_active ? "revoke" : "reinstate";
    const verb = c.is_active ? "Revoke" : "Reinstate";
    if (!window.confirm(`${verb} portal access for ${c.full_name}?`)) return;
    try {
      await axios.post(`${API}/building-managers/${c.id}/${action}/`);
      load();
    } catch {
      setError(`Could not ${action} that client user.`);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Client Users</div>
        <button style={styles.addButton} onClick={openAdd}>
          + Add Client User
        </button>
      </div>

      <div style={styles.content}>
        {error && (
          <div style={{ color: colors.red, fontSize: "13px", marginBottom: "12px" }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={styles.loading}>Loading…</div>
        ) : clients.length === 0 ? (
          <div
            style={{
              padding: "48px 24px",
              textAlign: "center",
              color: colors.slate,
              fontSize: "14px",
            }}
          >
            No client users yet. Click "+ Add Client User" to give someone access
            to their building.
          </div>
        ) : (
          <div style={styles.card}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: COLS,
                gap: "12px",
                padding: "8px 20px",
                backgroundColor: "#f8fafc",
                borderBottom: `1px solid ${colors.border}`,
                fontSize: "11px",
                fontWeight: 700,
                color: colors.slate,
                textTransform: "uppercase",
                letterSpacing: "0.4px",
              }}
            >
              <div>Name</div>
              <div>Username</div>
              <div>Email</div>
              <div>Sites</div>
              <div>Status</div>
              <div />
            </div>

            {clients.map((c) => (
              <div
                key={c.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  gap: "12px",
                  padding: "11px 20px",
                  borderBottom: `1px solid ${colors.border}`,
                  alignItems: "center",
                  backgroundColor: colors.white,
                }}
              >
                <div style={{ fontSize: "14px", color: colors.text, fontWeight: 500 }}>
                  {c.full_name}
                </div>
                <div style={{ fontSize: "13px", color: colors.slate }}>
                  {c.username}
                </div>
                <div style={{ fontSize: "13px", color: colors.slate }}>
                  {c.email || "—"}
                </div>
                <div
                  style={{ fontSize: "13px", color: colors.text }}
                  title={(c.site_names || []).join(", ")}
                >
                  {(c.site_names || []).join(", ") || "No sites"}
                </div>
                <div>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      padding: "3px 10px",
                      borderRadius: "20px",
                      backgroundColor: c.is_active ? "#dcfce7" : "#f1f5f9",
                      color: c.is_active ? "#15803d" : colors.slate,
                    }}
                  >
                    {c.is_active ? "Active" : "Revoked"}
                  </span>
                </div>
                <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }}>
                  <IconButton title="Edit" onClick={() => openEdit(c)}>
                    <LuPencil size={15} />
                  </IconButton>
                  <IconButton
                    title="Reset password"
                    onClick={() => {
                      setPwClient(c);
                      setNewPassword("");
                      setPwError("");
                    }}
                  >
                    <LuKeyRound size={15} />
                  </IconButton>
                  <IconButton
                    title={c.is_active ? "Revoke access" : "Reinstate access"}
                    onClick={() => toggleAccess(c)}
                  >
                    {c.is_active ? <LuUserX size={15} /> : <LuUserCheck size={15} />}
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <SimpleModal
          title={editing ? `Edit: ${editing.username}` : "New Client User"}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
          saving={saving}
          error={formError}
          saveLabel={editing ? "Save" : "Add"}
          wide
        >
          {!editing && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Username *</label>
              <input
                style={inputStyle}
                autoFocus
                value={form.username}
                onChange={(e) => set("username", e.target.value)}
                placeholder="john.manager"
              />
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>First Name</label>
              <input
                style={inputStyle}
                value={form.firstName}
                onChange={(e) => set("firstName", e.target.value)}
              />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Last Name</label>
              <input
                style={inputStyle}
                value={form.lastName}
                onChange={(e) => set("lastName", e.target.value)}
              />
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Email *</label>
            <input
              style={inputStyle}
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="john@strata.com.au"
            />
          </div>

          {!editing && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Password *</label>
              <input
                style={inputStyle}
                type="password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
              />
              <div style={{ fontSize: "12px", color: colors.slate, marginTop: "4px" }}>
                No email is sent — pass this on yourself. They can change it from
                the portal.
              </div>
            </div>
          )}

          <div style={fieldStyle}>
            <label style={labelStyle}>Sites they may access *</label>
            <div
              style={{
                maxHeight: "260px",
                overflowY: "auto",
                border: `1px solid ${colors.border}`,
                borderRadius: "8px",
              }}
            >
              {sites.map((s) => (
                <label
                  key={s.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "9px 12px",
                    borderBottom: `1px solid ${colors.border}`,
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.siteIds.includes(s.id)}
                    onChange={() => toggleSite(s.id)}
                  />
                  <span style={{ color: colors.text }}>{s.site_name}</span>
                </label>
              ))}
            </div>
            <div style={{ fontSize: "12px", color: colors.slate, marginTop: "6px" }}>
              {form.siteIds.length} selected. They will see nothing outside these.
            </div>
          </div>
        </SimpleModal>
      )}

      {pwClient && (
        <SimpleModal
          title={`Reset password: ${pwClient.username}`}
          onClose={() => setPwClient(null)}
          onSave={handleResetPassword}
          saving={pwSaving}
          error={pwError}
          saveLabel="Reset"
        >
          <div style={fieldStyle}>
            <label style={labelStyle}>New Password *</label>
            <input
              style={inputStyle}
              type="password"
              autoFocus
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <div style={{ fontSize: "12px", color: colors.slate, marginTop: "4px" }}>
              Their current password stops working straight away.
            </div>
          </div>
        </SimpleModal>
      )}
    </div>
  );
}

function blankForm() {
  return {
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    siteIds: [],
  };
}

// Grey throughout, including revoke: it is reversible and routine, not a
// hazard.
function IconButton({ title, onClick, children }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        color: colors.slate,
        padding: "4px",
        borderRadius: "4px",
        display: "flex",
        alignItems: "center",
      }}
    >
      {children}
    </button>
  );
}

// The API answers with field errors, a detail string, or a non-field list
// depending on what failed; surface whichever arrived rather than a generic.
function firstMessage(err, fallback) {
  const d = err.response?.data;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (d.detail) return d.detail;
  for (const value of Object.values(d)) {
    if (Array.isArray(value) && value.length) return value[0];
    if (typeof value === "string") return value;
  }
  return fallback;
}
